import { Box } from "@radix-ui/themes";
import { Layer as LayerLib } from "konva/lib/Layer";
import { Stage as StageLib } from "konva/lib/Stage";
import { useRef } from "react";
import { Layer, Stage, StageProps } from "react-konva";
import { useMeasure, useUpdateEffect } from "react-use";
import { twMerge } from "tailwind-merge";

type Dimensions = {
  width: number;
  height: number;
};

type Canvas2DWrapperProps = {
  size: Dimensions;
  renderUnderlay?: React.ReactNode;
  className?: string;
  onResize: (dimensions: Dimensions) => void;
  canvasLabel?: React.ReactNode;
} & StageProps;

export function Canvas2DWrapper({
  size,
  onResize: onCanvasResize,
  children,
  renderUnderlay,
  className,
  canvasLabel,
  ...props
}: React.PropsWithChildren<Canvas2DWrapperProps>) {
  const stageRef = useRef<StageLib | null>(null);
  const imageLayer = useRef<LayerLib | null>(null);
  const [canvasRef, rect] = useMeasure<HTMLDivElement>();

  useUpdateEffect(() => {
    if (rect.width && rect.height && imageLayer.current) {
      const currentAspectRatio = rect.width / rect.height;
      if (currentAspectRatio > 16 / 9) {
        onCanvasResize({
          width: rect.height * (16 / 9),
          height: rect.height,
        });
      } else {
        onCanvasResize({
          width: rect.width,
          height: rect.width / (16 / 9),
        });
      }

      imageLayer.current?.offsetY(0);
      imageLayer.current?.offsetX(0);
    }
  }, [rect]);

  return (
    <Box
      ref={canvasRef}
      className={twMerge(["canvas-wrapper h-full relative z-20", className])}
    >
      {renderUnderlay}
      <Stage
        tabIndex={0}
        ref={stageRef}
        className={twMerge([
          "stage absolute top-0 left-0 w-full h-full outline-none flex justify-center items-center",
        ])}
        width={size.width}
        height={size.height}
        onContextMenu={(e) => {
          e.evt.preventDefault();
          return false;
        }}
        {...props}
      >
        <Layer ref={imageLayer}>{children}</Layer>
      </Stage>
      {canvasLabel && (
        <div
          className="text-white absolute -translate-y-[100%] left-1"
          style={{
            top: `calc(50% - ${Math.floor(size.height / 2)}px - 2px)`,
          }}
        >
          {canvasLabel}
        </div>
      )}
    </Box>
  );
}
